import React, { useEffect, useMemo, useState } from 'react';
import { Pill, Search, UserRound } from 'lucide-react';
import { useToast } from '../../Common/context/ToastContext';
import { getCpCustomerList, getCpTerminalList } from '../api/cp';

const PAGE_SIZE = 50;

function normalizeTerminalDirectory(data) {
  const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
  const directory = new Map();
  if (!Array.isArray(rows)) return directory;
  rows.forEach((row) => {
    const terminal = {
      name: row.name ?? row.terminalName ?? row.terminal_name ?? '',
      loginId: row.terminalId ?? row.loginId ?? row.login_id ?? row.terminal_id ?? row.code ?? '',
    };
    [row.id, row.terminalPk, row.terminal_pk, row.terminalId, row.terminal_id, row.code].forEach((key) => {
      if (key !== undefined && key !== null && key !== '') directory.set(String(key), terminal);
    });
  });
  return directory;
}

function normalizeCustomerList(data, terminalDirectory) {
  const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => {
    const terminal = row.terminal ?? row.salesperson ?? row.driver ?? {};
    const driverText = typeof row.driver === 'string' ? row.driver : '';
    const terminalPk = row.terminalPk ?? row.terminal_pk ?? row.terminal_id ?? row.terminalId ?? terminal.id;
    const assignedTerminal = terminalDirectory.get(String(terminalPk ?? ''));
    const directDriverName = row.driverName ?? row.driver_name ?? row.salespersonName ?? row.salesperson_name ?? row.terminalName ?? row.terminal_name ?? terminal.name ?? driverText;
    const directDriverLoginId = row.driverLoginId ?? row.driver_login_id ?? row.salespersonLoginId ?? row.salesperson_login_id ?? row.terminalLoginId ?? row.terminal_login_id ?? terminal.terminalId ?? terminal.terminal_id ?? '';
    return {
      id: row.mappingId ?? row.mapping_id ?? row.id ?? row.pharmacyId ?? row.pharmacy_id ?? index,
      code: row.externalCustomerCode ?? row.external_customer_code ?? row.institutionCode ?? row.institution_code ?? row.code ?? '',
      name: row.name ?? row.customerName ?? row.customer_name ?? row.pharmacyName ?? row.pharmacy_name ?? '',
      representative: row.representativeName ?? row.representative_name ?? row.representative ?? row.owner ?? '',
      businessNo: row.businessNo ?? row.business_no ?? '',
      phone: row.phone ?? row.pharmacyPhone ?? row.pharmacy_phone ?? row.tel ?? '',
      address: row.address ?? row.addr ?? '',
      status: String(row.status ?? 'ACTIVE').toUpperCase(),
      driverName: directDriverName || assignedTerminal?.name || '',
      driverLoginId: directDriverLoginId || assignedTerminal?.loginId || '',
    };
  });
}

function formatPhone(value) {
  const numbers = String(value ?? '').replace(/[^0-9]/g, '');
  if (!numbers) return '-';
  if (numbers.startsWith('02')) {
    if (numbers.length <= 9) return numbers.replace(/^(02)(\d{3})(\d{4})$/, '$1-$2-$3');
    return numbers.replace(/^(02)(\d{4})(\d{4})$/, '$1-$2-$3');
  }
  return numbers.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
}

function PharmacyAssignmentManage() {
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { page, limit: PAGE_SIZE };
        if (debouncedQuery) params.q = debouncedQuery;
        const [data, terminalData] = await Promise.all([
          getCpCustomerList(params),
          getCpTerminalList({ page: 1, limit: 500 }),
        ]);
        if (cancelled) return;
        const nextRows = normalizeCustomerList(data, normalizeTerminalDirectory(terminalData));
        setRows(nextRows);
        setTotal(Number(data?.total ?? data?.count) || nextRows.length);
        setSelected((previous) => {
          if (previous && nextRows.some((row) => row.id === previous.id)) {
            return nextRows.find((row) => row.id === previous.id);
          }
          return nextRows[0] ?? null;
        });
      } catch (error) {
        if (!cancelled) {
          setRows([]);
          setTotal(0);
          setSelected(null);
          showToast(error?.message || '약국 목록을 불러오지 못했습니다.', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [debouncedQuery, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastPage = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  return (
    <div className="flex-1 min-h-0 overflow-hidden bg-white px-8 py-4">
      <div className="h-full min-h-0 flex flex-col gap-3">
        <div className="shrink-0 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-[20px] font-extrabold text-[#191f28]">약국별 담당 영업자</h1>
            <p className="mt-1 text-[13px] text-[#8b95a1]">약국을 기준으로 현재 할당된 영업자를 확인합니다.</p>
          </div>
          <span className="text-[13px] font-semibold text-[#6b7684]">총 {total.toLocaleString('ko-KR')}곳</span>
        </div>

        <div className="shrink-0 h-[42px] w-[360px] flex items-center gap-2 rounded-lg border border-transparent bg-[#f2f4f6] px-3 focus-within:border-[var(--brand-primary)]">
          <Search size={17} className="shrink-0 text-[#8b95a1]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="거래처코드, 약국명, 사업자번호 검색"
            className="min-w-0 flex-1 border-0 bg-transparent text-[14px] text-[#191f28] outline-none"
          />
        </div>

        <div className="flex-1 min-h-0 flex gap-5">
          <section className="flex-[6] min-w-[680px] overflow-hidden rounded-2xl border border-[#e0e0e0] bg-white shadow-md flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <table className="w-full table-fixed text-left text-[13px]">
                <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#8b95a1]">
                  <tr className="border-b border-[#f2f4f6]">
                    <th className="w-[120px] px-4 py-3">거래처코드</th>
                    <th className="px-4 py-3">약국명</th>
                    <th className="w-[160px] px-4 py-3">담당 영업자</th>
                    <th className="w-[140px] px-4 py-3">사업자번호</th>
                    <th className="w-[70px] px-4 py-3 text-center">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f2f4f6]">
                  {loading ? (
                    <tr><td colSpan={5} className="px-4 py-12 text-center text-[#8b95a1]">약국 목록을 불러오는 중입니다.</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-12 text-center text-[#8b95a1]">조회된 약국이 없습니다.</td></tr>
                  ) : rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => setSelected(row)}
                      className={`h-[42px] cursor-pointer transition-colors ${selected?.id === row.id ? 'bg-[var(--brand-primary-soft)]' : 'hover:bg-[#f9fafb]'}`}
                    >
                      <td className="truncate px-4 py-2 font-mono" title={row.code || '-'}>{row.code || '-'}</td>
                      <td className="truncate px-4 py-2 font-bold text-[#191f28]" title={row.name || '-'}>{row.name || '-'}</td>
                      <td className="truncate px-4 py-2 font-semibold text-[#4e5968]" title={row.driverName || row.driverLoginId || '미할당'}>
                        {row.driverName || row.driverLoginId || <span className="text-[#adb5bd]">미할당</span>}
                      </td>
                      <td className="truncate px-4 py-2 text-[#4e5968]">{row.businessNo || '-'}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${row.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {row.status === 'ACTIVE' ? '활성' : '비활성'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div className="shrink-0 flex items-center justify-center gap-3 border-t border-[#f2f4f6] px-4 py-3 text-[13px]">
                <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))} className="rounded-lg border border-[#e5e8eb] px-3 py-1.5 font-bold text-[#4e5968] disabled:opacity-40">이전</button>
                <span className="font-semibold text-[#6b7684]">{page} / {lastPage}</span>
                <button type="button" disabled={page >= lastPage || loading} onClick={() => setPage((value) => Math.min(lastPage, value + 1))} className="rounded-lg border border-[#e5e8eb] px-3 py-1.5 font-bold text-[#4e5968] disabled:opacity-40">다음</button>
              </div>
            )}
          </section>

          <section className="flex-[4] min-w-[360px] overflow-hidden rounded-2xl border border-[#e0e0e0] bg-white shadow-md flex flex-col">
            <div className="h-[58px] shrink-0 flex items-center border-b border-[#f2f4f6] bg-[#fcfdfe] px-5">
              <h2 className="text-[18px] font-extrabold text-[#191f28]">약국 상세</h2>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-6">
              {!selected ? (
                <div className="h-full flex flex-col items-center justify-center gap-4 text-[#adb5bd]">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f2f4f6]"><Pill size={30} className="opacity-30" /></div>
                  <p className="text-center text-[15px] font-bold">목록에서 약국을 선택해 주세요.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="rounded-xl border border-[var(--brand-primary-border)] bg-[var(--brand-primary-soft)] p-4">
                    <div className="mb-2 flex items-center gap-2 text-[13px] font-bold text-[var(--brand-primary)]"><UserRound size={16} /> 담당 영업자</div>
                    <p className="text-[18px] font-extrabold text-[#191f28]">{selected.driverName || '미할당'}</p>
                    {selected.driverLoginId && <p className="mt-1 font-mono text-[13px] text-[#6b7684]">{selected.driverLoginId}</p>}
                  </div>
                  <DetailRow label="거래처코드" value={selected.code} />
                  <DetailRow label="약국명" value={selected.name} />
                  <DetailRow label="대표자명" value={selected.representative} />
                  <DetailRow label="사업자번호" value={selected.businessNo} />
                  <DetailRow label="연락처" value={formatPhone(selected.phone)} />
                  <DetailRow label="주소" value={selected.address} multiline />
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, multiline = false }) {
  return (
    <div className={`flex gap-4 ${multiline ? 'items-start' : 'items-center'}`}>
      <span className="w-[96px] shrink-0 text-[13px] font-bold text-[#8b95a1]">{label}</span>
      <span className={`min-w-0 flex-1 text-right text-[15px] font-bold text-[#191f28] ${multiline ? 'whitespace-pre-wrap break-words' : 'truncate'}`} title={String(value || '')}>{value || '-'}</span>
    </div>
  );
}

export default PharmacyAssignmentManage;

